import { ListChecks, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { monthDayLabel } from '../lib/format';
import type { ParentTask } from '../lib/types';
import { useParent } from './ParentContext';

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The parents' own to-do list on the Home page (#332): a shared checklist with optional due dates. */
export function ParentTasks() {
  const { toast } = useParent();
  const [tasks, setTasks] = useState<ParentTask[] | null>(null);
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<ParentTask[]>('/api/tasks')
      .then((r) => live && setTasks(r))
      .catch(() => live && setTasks([]));
    return () => {
      live = false;
    };
  }, []);

  const fail = (e: unknown) => toast({ text: (e as Error).message, tone: 'alert' });
  const sortOpen = (list: ParentTask[]) =>
    [...list].sort((a, b) => (!a.dueDate ? 1 : 0) - (!b.dueDate ? 1 : 0) || a.dueDate.localeCompare(b.dueDate) || a.createdAt - b.createdAt);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const t = await api.post<ParentTask>('/api/tasks', { title: title.trim(), dueDate: due });
      setTasks((list) => [...(list ?? []), t]);
      setTitle('');
      setDue('');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function toggle(t: ParentTask, done: boolean) {
    try {
      const next = await api.patch<ParentTask>(`/api/tasks/${t.id}`, { done });
      setTasks((list) => list?.map((x) => (x.id === t.id ? next : x)) ?? null);
    } catch (err) {
      fail(err);
    }
  }

  async function remove(t: ParentTask) {
    try {
      await api.del(`/api/tasks/${t.id}`);
      setTasks((list) => list?.filter((x) => x.id !== t.id) ?? null);
    } catch (err) {
      fail(err);
    }
  }

  if (tasks === null) return null;
  const today = todayIso();
  const open = sortOpen(tasks.filter((t) => !t.doneAt));
  const done = tasks.filter((t) => t.doneAt).sort((a, b) => (b.doneAt ?? 0) - (a.doneAt ?? 0));

  const row = (t: ParentTask) => (
    <li key={t.id} className={`task-row${t.doneAt ? ' done' : ''}`}>
      <label className="check grow">
        <input type="checkbox" checked={!!t.doneAt} onChange={(e) => toggle(t, e.target.checked)} />
        <span>
          <span className="task-title">{t.title}</span>
          {t.doneAt ? (
            <span className="muted small block">Done{t.doneBy ? ` by ${t.doneBy}` : ''}</span>
          ) : (
            t.dueDate && (
              <span className={`small block ${t.dueDate < today ? 'task-overdue' : t.dueDate === today ? 'task-today' : 'muted'}`}>
                {t.dueDate < today ? 'Overdue · ' : t.dueDate === today ? 'Due today · ' : 'Due '}
                {monthDayLabel(t.dueDate.slice(5))}
              </span>
            )
          )}
        </span>
      </label>
      <button className="icon-btn" onClick={() => remove(t)} aria-label={`Delete ${t.title}`}>
        <Trash2 size={15} />
      </button>
    </li>
  );

  return (
    <section className="card parent-tasks stack">
      <h2 className="with-icon">
        <ListChecks size={18} /> To-do list
      </h2>
      <form className="task-add" onSubmit={add}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Add a task, e.g. Order next term’s math books" aria-label="New task" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date (optional)" title="Due date (optional)" />
        <button className="btn primary" disabled={busy || !title.trim()}>
          <Plus size={16} /> Add
        </button>
      </form>
      {open.length ? (
        <ul className="task-list">{open.map(row)}</ul>
      ) : (
        <p className="muted small">Nothing to do. Tasks you add here are shared with everyone in your family.</p>
      )}
      {done.length > 0 && (
        <>
          <button className="btn small task-done-toggle" onClick={() => setShowDone((s) => !s)}>
            {showDone ? 'Hide' : 'Show'} done ({done.length})
          </button>
          {showDone && <ul className="task-list">{done.map(row)}</ul>}
        </>
      )}
    </section>
  );
}
